// =====================================================================
// content.js — single source of truth for the public marketing pages.
// Everything the home / features / pricing screens render lives here so
// copy stays consistent across pages. Sourced from docs/pricing.md and
// docs/BUILD.md — keep numbers in sync with those documents.
// =====================================================================

export const PLATFORM = {
  name: 'during.uk Client Portal',
  shortName: 'during.uk',
  tagline: 'The client portal platform built for UK heating & plumbing'
};

export const STATS = [
  { value: '3', label: 'portals in one platform — admin, client, engineer' },
  { value: '30+', label: 'screens across the whole platform' },
  { value: '24+', label: 'PocketBase collections — jobs to certificates' },
  { value: '0', label: 'passwords your clients ever need to remember' }
];

// The five unique differentiators (pricing.md §3) — no competitor has any.
export const UNIQUE = [
  {
    icon: 'Layers',
    title: 'True multi-tenant',
    text: 'One deployment, many agencies. Your branding, colours and logo — tenant-isolated data on every record.'
  },
  {
    icon: 'Shield',
    title: 'Statutory gas certificates',
    text: '7 gas certificate types plus EICR, generated as Gas Safe-compliant PDFs. Regulation 36(3) content is enforced, not optional.'
  },
  {
    icon: 'Zap',
    title: 'Passwordless clients',
    text: 'One-click magic links — an 8-digit code by email, 30-day sessions, no passwords, no forgotten-password support calls.'
  },
  {
    icon: 'Send',
    title: 'Telegram-first alerts',
    text: 'Instant Telegram + email the moment a client messages, approves a quote or declares a BACS payment. SMS escalation optional.'
  },
  {
    icon: 'Cloud',
    // TODO(3.3): restore stronger offline claim (offline mode / dead zones) after
    // the offline sync QA sign-off — currently only draft autosave is live.
    title: 'Engineers autosave on-site',
    text: 'Installable PWA — job sheet drafts autosave as the engineer works, so a browser crash mid-job never loses the sheet.'
  }
];

// Three-portal overview cards (home page).
export const PORTALS = [
  {
    icon: 'Settings',
    title: 'Admin console',
    text: 'Everything the office runs on: a single dashboard for clients, jobs, money and messages.',
    items: [
      'Clients, properties, jobs & schedule board',
      'Invoices, estimates & quotes with PDF generation',
      'Payments — Stripe card, BACS, cash, cheque',
      'Team accounts with role-based access'
    ]
  },
  {
    icon: 'Users',
    title: 'Client portal',
    text: 'A branded, one-click home for every customer — no passwords, no friction.',
    items: [
      'Invoices, estimates & quotes with online payment',
      'Full job history with photos and certificates',
      'Documents with read-receipt acknowledgements',
      'Live chat — office notified instantly'
    ]
  },
  {
    icon: 'Tool',
    title: 'Engineer portal',
    text: 'A mobile-first PWA your engineers install on their phone and use all day.',
    items: [
      'Digital job sheets with client signatures',
      '7 gas certificate types + EICR on-site',
      'GPS mapping — Google Maps, Waze & Apple Maps',
      'Parts, faults & expenses databases'
    ]
  }
];

// Home page deep-dive rows (alternating split layout).
export const SPLITS = [
  {
    icon: 'FileText',
    title: 'Statutory certificates without the paper',
    text: 'Generate Landlord Gas Safety Records (CP12), Gas Safety Records, EICR and more as compliant PDFs on site. Every certificate auto-numbers, auto-files against the property and auto-creates the renewal key date — with a reminder cron that notifies you before expiry.',
    points: [
      'LGSR / CP12 with Regulation 36(3) minimum content enforced',
      'Gas Safe registration number format-checked before issue',
      'Safety classifications: ID / AR / NCS / Safe to use',
      'Certificate → key date → reminder, fully automatic'
    ],
    panel: [
      { icon: 'FileText', title: 'Landlord Gas Safety Record', sub: 'CP12 · statutory · annual renewal' },
      { icon: 'FileText', title: 'Gas Safety Record + EICR', sub: 'Owner-occupied & electrical' },
      { icon: 'Bell', title: 'Renewal key dates', sub: 'Daily cron · Telegram + email reminders' }
    ]
  },
  {
    icon: 'CreditCard',
    title: 'Get paid faster, chase less',
    text: 'Send branded invoice PDFs, take card payments through Stripe, and let clients declare BACS transfers themselves. Quote approved → invoice raised in one click. The office sees every payment status in real time.',
    points: [
      'Stripe Connect card payments on any invoice',
      '“I’ve paid by BACS” self-service claims',
      'Quotes convert to invoices in one click',
      'Outstanding balances tracked per client'
    ],
    panel: [
      { icon: 'CreditCard', title: 'Card payments', sub: 'Stripe Connect · server-side keys' },
      { icon: 'Cash', title: 'BACS claims', sub: 'Client-declared · agency-confirmed' },
      { icon: 'FileText', title: 'Invoice PDFs', sub: 'Branded · £-correct · PDFKit' }
    ]
  },
  {
    icon: 'MessageCircle',
    title: 'Messages that reach you instantly',
    text: 'When a client sends a message, approves a quote or requests a job, the agency is notified over Telegram and email within seconds — with an audit trail of every notification sent. No inbox-watching, no missed leads.',
    points: [
      'Telegram bot alerts in real time',
      'Email fallback on every notification',
      'SMS escalation when it matters',
      'Append-only notification audit log'
    ],
    panel: [
      { icon: 'Send', title: 'Telegram alerts', sub: 'Bot API · primary channel' },
      { icon: 'Bell', title: 'Email fallback', sub: 'SMTP · always parallel' },
      { icon: 'CheckCircle', title: 'Audit log', sub: 'Append-only · every send recorded' }
    ]
  }
];

// Features page — full grouped inventory (BUILD.md capability list).
export const FEATURE_GROUPS = [
  {
    icon: 'Settings',
    title: 'Admin console',
    items: [
      'Dashboard with live outstanding balances and unread messages',
      'Clients — profiles, properties, notes, key dates, documents',
      'Jobs — priority, scheduling, engineer assignment, properties',
      'Schedule board with calendar view',
      'Quotes & estimates — draft, send, approve, convert to invoice',
      'Invoices — numbering, tax, payment terms, PDF generation',
      'Payments — card / BACS / cash / cheque with status workflow',
      'Certificates overview across every client and property',
      'Messages inbox — filter by client, engineer and unread',
      'Engineer back-office — job sheets, files, parts, faults, expenses, mapping',
      'Review requests with click tracking',
      'Settings — branding, invoice defaults, key-date types, team, portal links'
    ]
  },
  {
    icon: 'Users',
    title: 'Client portal',
    items: [
      'One-click magic-link sign-in — 8-digit code by email, 30-day session',
      'Invoices, estimates and quotes with online card payment',
      'Full job history with photos and certificates',
      'Documents library with versioning and read acknowledgements',
      'Key dates — service reminders and certificate renewals',
      'Live chat with the office (realtime)',
      'Self-service: request a job, request a reschedule, declare a BACS payment',
      'Your branding — colours and logo applied to every client’s portal'
    ]
  },
  {
    icon: 'Tool',
    title: 'Engineer portal',
    items: [
      'Job sheets — draft → in progress → completed → submitted workflow',
      'Digital client signatures on site',
      'Certificates — 7 gas types + EICR, statutory PDF generation',
      'GPS mapping with Google Maps / Waze / Apple Maps deep links',
      'Live ETA updates pushed to the client',
      'Parts database with stock levels and suppliers',
      'Faults database — troubleshooting codes and solutions',
      'Expenses — mileage, fuel, tolls, other, with CSV export',
      'Engineer files — manuals and specs pushed from the office',
      // TODO(3.3): restore 'background sync' claim after QA sign-off
      'PWA with draft autosave'
    ]
  },
  {
    icon: 'Zap',
    title: 'Payments & money',
    items: [
      'Stripe Connect card checkout on any invoice',
      'BACS transfer claims with agency confirmation workflow',
      'Payment methods: bank transfer, card, cash, cheque',
      'Quote → invoice conversion in one click',
      'Outstanding balance tracking per client and across the agency',
      'Branded invoice PDFs with correct £ symbols'
    ]
  },
  {
    icon: 'Bell',
    title: 'Notifications',
    items: [
      'Telegram bot — the primary alert channel for the office',
      'Email — parallel fallback on every event',
      'SMS — optional escalation channel',
      'Events: client message, engineer message, quote approved, job requested, reschedule requested, BACS payment declared, job sheet completed',
      'Daily 08:00 UTC key-date reminder cron',
      'Delayed Google-review request emails after job completion',
      'Append-only notification audit log'
    ]
  },
  {
    icon: 'Sparkle',
    title: 'AI assistance',
    items: [
      'AI-suggested replies to client messages',
      'AI-assisted quote drafting from a job description',
      'One-paragraph client summaries for the inbox',
      'Grounded in your own per-tenant price catalogue',
      'Works deterministically without an LLM key'
    ]
  },
  {
    icon: 'MapPin',
    title: 'Mapping & GPS',
    items: [
      'Job map with all scheduled work plotted',
      'Address autocomplete via Photon geocoding',
      'One-tap navigation: Google Maps, Waze, Apple Maps',
      'Live engineer location + activity panel',
      'Weather strip on the engineer dashboard'
    ]
  },
  {
    icon: 'Shield',
    title: 'Security & audit',
    items: [
      'Magic-link (OTP) auth for clients — single-use 10-minute codes',
      'Role-based access: agency admin, agency staff, client, engineer',
      'Tenant isolation on every collection — clients never cross tenants',
      'Append-only audit trail — logins, views, downloads, acknowledgements',
      'Portal visit tracking per client',
      'Short-lived file tokens for protected documents',
      'TLS everywhere via Caddy + Let’s Encrypt'
    ]
  },
  {
    icon: 'Cloud',
    title: 'Platform & PWA',
    items: [
      'Installable PWA — add the engineer portal to a home screen',
      'Service worker caching (v3) — app shell precached',
      // TODO(3.3): restore offline-mode claim after QA sign-off (sync is
      // scaffolding no screen uses yet; autosave recovery is a failing test)
      'Draft autosave for job sheets',
      'Realtime updates via PocketBase subscriptions',
      'Multi-tenant — one deployment serves many agencies',
      'Per-tenant branding: colours and logo',
      'Modern stack: React 18, Vite 5, PocketBase 0.39, Node 20, FastAPI'
    ]
  }
];

// Pricing tiers (docs/pricing.md §4) — the pricing page renders these
// directly; keep amounts in sync with the document.
export const TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    target: 'Sole traders needing a client portal + digital invoicing',
    monthly: 49,
    annual: 499,
    annualNote: 'per year — save ~15%',
    features: [
      'Client portal — up to 50 clients',
      'Admin console: clients, jobs, invoices, key dates',
      '2 staff accounts (extra £10/mo each)',
      'Email notifications',
      'Invoice PDF generation + BACS tracking',
      'Per-agency branding'
    ],
    cta: 'Start with Starter'
  },
  {
    id: 'professional',
    name: 'Professional',
    target: 'Growing agencies with 3–10 field engineers — the sweet spot',
    monthly: 129,
    annual: 1299,
    annualNote: 'per year — save ~16%',
    featured: true,
    features: [
      'Everything in Starter, plus:',
      'Up to 250 clients · 5 staff accounts',
      'Engineer portal — job sheets, signatures, mapping, ETA',
      'Gas & electrical certificates (7 types + EICR)',
      'Stripe card payments',
      'Telegram notifications',
      'Schedule board + client self-service',
      'Google Review automation',
      'Parts & faults databases',
      'PWA shell'
    ],
    cta: 'Choose Professional'
  },
  {
    id: 'business',
    name: 'Business',
    target: 'Larger agencies, 10–50 engineers or multi-branch',
    monthly: 299,
    annual: 2999,
    annualNote: 'per year — save ~17%',
    features: [
      'Everything in Professional, plus:',
      'Unlimited clients & staff',
      'Up to 25 engineer accounts (extra £8/mo each)',
      'AI assistant — suggested replies & quote drafting',
      'Reduced payment fees',
      'Advanced reporting',
      // TODO(3.3): restore 'Full offline sync' when shipped + QA-verified
      'Priority on-site data handling',
      'Priority support (4h)',
      'API access + webhooks',
      'Custom subdomain'
    ],
    cta: 'Choose Business'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    target: 'Groups, housing associations, national chains',
    monthly: null,
    annual: null,
    annualNote: 'from £599/mo — bespoke',
    features: [
      'Everything in Business, plus:',
      'Dedicated infrastructure',
      '99.5% uptime SLA',
      'Custom integrations — Sage, Xero, CIS',
      'White-label domain',
      'SMS as primary channel',
      'On-site training',
      'Dedicated account manager',
      'Custom development — 5 dev days/mo included'
    ],
    cta: 'Talk to us'
  }
];

// Feature comparison (pricing.md §3) — values: true / false / string.
export const COMPARISON = [
  { feature: 'Client portal (invoices, docs, chat)', us: true, jobber: true, hcp: true, s8: true, simpro: true },
  { feature: 'Multi-tenant (one deployment, many agencies)', us: true, jobber: false, hcp: false, s8: false, simpro: false },
  { feature: 'Field engineer mobile app', us: true, jobber: true, hcp: true, s8: true, simpro: true },
  { feature: 'Digital signatures on mobile', us: true, jobber: 'Limited', hcp: 'Limited', s8: true, simpro: true },
  { feature: 'Statutory gas certificates (7 types + EICR)', us: true, jobber: false, hcp: false, s8: false, simpro: false },
  // TODO(3.3): restore 'Offline mode' row (us: true) after QA sign-off — only
  // draft autosave is live today; full offline sync is not shipped.
  { feature: 'Draft autosave', us: true, jobber: 'Limited', hcp: 'Limited', s8: 'Partial', simpro: false },
  { feature: 'OTP magic-link auth (no client passwords)', us: true, jobber: false, hcp: false, s8: false, simpro: false },
  { feature: 'AI-assisted quoting', us: true, jobber: false, hcp: false, s8: false, simpro: false },
  { feature: 'Telegram bot notifications', us: true, jobber: false, hcp: false, s8: false, simpro: false },
  { feature: 'Stripe payments', us: true, jobber: true, hcp: true, s8: true, simpro: true },
  { feature: 'GPS multi-platform navigation', us: true, jobber: false, hcp: false, s8: 'Limited', simpro: 'Limited' },
  { feature: 'Parts & faults database', us: true, jobber: false, hcp: false, s8: false, simpro: 'Partial' },
  { feature: 'Google Review request automation', us: true, jobber: false, hcp: false, s8: false, simpro: false },
  { feature: 'Per-tenant branding & themes', us: true, jobber: false, hcp: false, s8: false, simpro: false },
  { feature: 'Portal visit audit trail', us: true, jobber: false, hcp: false, s8: false, simpro: false },
  { feature: 'Client self-service (job & reschedule requests, BACS claims)', us: true, jobber: false, hcp: false, s8: false, simpro: false },
  { feature: 'Installable PWA', us: true, jobber: false, hcp: false, s8: true, simpro: false }
];

export const FAQS = [
  {
    q: 'Do my clients need to create accounts or remember passwords?',
    a: 'No. Each client gets a one-click magic link by email. The link opens their portal and starts a 30-day session — no passwords at any point. When it expires, you resend a fresh link from the admin console in one click.'
  },
  {
    q: 'Are the gas certificates actually compliant?',
    a: 'Yes. Statutory certificate types (LGSR/CP12 and Gas Safety Record) enforce the Gas Safety (Installation and Use) Regulations 1998 Regulation 36(3) minimum content — the system refuses to generate a PDF with missing statutory fields (HTTP 422) rather than issuing a non-compliant document. Gas Safe registration numbers are format-checked before issue.'
  },
  {
    q: 'How do payments work?',
    a: 'Card payments run through Stripe Connect — keys stay server-side and card data never touches the platform. Clients can also declare BACS transfers themselves, which the agency confirms in the console. Payment methods supported: bank transfer, card, cash and cheque.'
  },
  {
    q: 'Does it work without signal on site?',
    a: 'The engineer portal is an installable PWA with draft autosave — job sheets in progress survive a browser crash or accidental close and are there when the engineer reopens the page. (Offline sync that queues completed work without signal is on the roadmap; ask us for status.)'
  },
  {
    q: 'Can I use my own branding?',
    a: 'Yes — colours and logo are per-agency, applied to every client portal automatically. Enterprise adds a full white-label domain.'
  },
  {
    q: 'What happens to my data if I leave?',
    a: 'Your data is yours — full PocketBase export on cancellation. Certificates, invoices and client history download with you.'
  },
  {
    q: 'Is there a contract or setup fee?',
    a: 'Monthly billing, no long-term contract, no setup fee. Annual billing saves 15–17% depending on tier.'
  }
];
